import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllSlotGames, SlotGameItem, launchGame as launchSlotGame } from '../services/slotService';
import { getAllGames, GameItem, launchGame as launchCasinoGame } from '../services/casinoService';
import { useAuth } from '../contexts/AuthContext';
import { useLoginModal } from '../contexts/LoginModalContext';
import { useNotifications } from '../hooks/useNotifications';
import { useGameLaunchMaintenance } from '../hooks/useGameLaunchMaintenance';

interface CategorySection {
  id: string;
  title: string;
  icon: string;
  games: (SlotGameItem | GameItem)[];
  navigateTo: string;
  type: 'slot' | 'casino';
}

const GameCategories: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const { openLoginModal } = useLoginModal();
  const { casinoBlocked, slotBlocked } = useGameLaunchMaintenance();
  const { error: showError, success, warning } = useNotifications();
  
  const [categories, setCategories] = useState<CategorySection[]>([]);
  const [loading, setLoading] = useState(true);
  const [launchingGameId, setLaunchingGameId] = useState<string | null>(null);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const [slotResponse, casinoResponse] = await Promise.all([
          getAllSlotGames({ perPage: 100, sort: 'popular' }),
          getAllGames({ perPage: 100, sort: 'popular' })
        ]);

        const slotGames = slotResponse.games || [];
        const casinoGames = casinoResponse.games || [];

        // 4 categories
        const sectionTitleKeys: Record<string, string> = {
          'popular-slots': 'landing.gameSections.popularSlots',
          'live-casino': 'landing.gameSections.liveCasino',
          'new-slots': 'landing.gameSections.newSlots',
          'table-games': 'landing.gameSections.tableGames',
        };
        const sections: CategorySection[] = [
          {
            id: 'popular-slots',
            title: t(sectionTitleKeys['popular-slots']),
            icon: '🔥',
            games: slotGames.slice(0, 10),
            navigateTo: '/slot',
            type: 'slot' as const,
          },
          {
            id: 'live-casino',
            title: t(sectionTitleKeys['live-casino']),
            icon: '🎥',
            games: casinoGames.slice(0, 10),
            navigateTo: '/casino',
            type: 'casino' as const,
          },
          {
            id: 'new-slots',
            title: t(sectionTitleKeys['new-slots']),
            icon: '✨',
            games: slotGames.slice(10, 20),
            navigateTo: '/slot',
            type: 'slot' as const,
          },
          {
            id: 'table-games',
            title: t(sectionTitleKeys['table-games']),
            icon: '🎲',
            games: casinoGames.slice(10, 20),
            navigateTo: '/casino',
            type: 'casino' as const,
          },
        ].filter(cat => cat.games.length > 0);

        setCategories(sections);
      } catch (err) {
        console.error('Failed to fetch games:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [t, i18n.language]);

  const handleGameClick = async (game: SlotGameItem | GameItem, type: 'slot' | 'casino') => {
    if (!user || !token) {
      openLoginModal();
      return;
    }

    if (type === 'slot' && slotBlocked) {
      warning(t('slotGames.maintenanceTitle'), t('slotGames.maintenanceMessage'), 8000);
      return;
    }
    if (type === 'casino' && casinoBlocked) {
      warning(t('liveCasino.maintenanceTitle'), t('liveCasino.maintenanceMessage'), 8000);
      return;
    }

    setLaunchingGameId(game.id);
    try {
      let result;
      if (type === 'slot') {
        const slotGame = game as SlotGameItem;
        result = await launchSlotGame({
          id: slotGame.id,
          game_id: slotGame.game_id,
          code: slotGame.code,
          vendor: slotGame.provider,
          provider: slotGame.provider,
          api: slotGame.provider,
          names: slotGame.names,
          image: slotGame.image,
          key: slotGame.key,
        }, user.loginId || '', token || '');
      } else {
        const casinoGame = game as GameItem;
        result = await launchCasinoGame({
          id: casinoGame.id,
          vendor: casinoGame.provider,
          api: casinoGame.provider,
          code: casinoGame.code,
          names: casinoGame.names,
          img: casinoGame.image,
          mobileimg: casinoGame.image,
          title: casinoGame.names.en,
          help: '',
        }, user.loginId || '', token || '');
      }

      if (result?.result?.link) {
        success(t('common.gameStarted'), '');
        navigate('/play', { state: { gameUrl: result.result.link, title: getGameName(game), backPath: type === 'slot' ? '/slot' : '/casino' } });
      }
    } catch (err: any) {
      showError(t('common.error'), String(err.message || t('common.tryAgain')));
    } finally {
      setLaunchingGameId(null);
    }
  };

  const getGameName = (game: SlotGameItem | GameItem) => {
    return i18n.language === 'ko' ? game.names.ko : game.names.en;
  };

  if (loading) {
    return (
      <div className="py-6 sm:py-8 section-gradient w-full overflow-hidden">
        <div className="w-full max-w-[1800px] mx-auto px-3 sm:px-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="mb-6 sm:mb-8 md:mb-10">
              <div className="h-5 sm:h-6 md:h-8 bg-dark-700 rounded-lg w-28 sm:w-36 md:w-48 mb-3 sm:mb-4 md:mb-5 animate-pulse"></div>
              <div className="flex gap-2 sm:gap-3 md:gap-4 overflow-hidden">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                  <div key={j} className="w-[100px] sm:w-[120px] md:w-[140px] lg:w-[150px] h-[133px] sm:h-[160px] md:h-[187px] lg:h-[200px] bg-dark-700 rounded-lg sm:rounded-xl md:rounded-2xl animate-pulse flex-shrink-0"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 sm:py-8 section-gradient w-full overflow-hidden">
      <div className="w-full max-w-[1800px] mx-auto space-y-8 sm:space-y-10">
        {categories.map(category => (
          <CategoryRow
            key={category.id}
            category={category}
            onGameClick={(game) => handleGameClick(game, category.type)}
            onAllClick={() => navigate(category.navigateTo)}
            getGameName={getGameName}
            launchingGameId={launchingGameId}
          />
        ))}
      </div>
    </div>
  );
};

interface CategoryRowProps {
  category: CategorySection;
  onGameClick: (game: SlotGameItem | GameItem) => void;
  onAllClick: () => void;
  getGameName: (game: SlotGameItem | GameItem) => string;
  launchingGameId: string | null;
}

const CategoryRow: React.FC<CategoryRowProps> = ({ 
  category, 
  onGameClick, 
  onAllClick, 
  getGameName,
  launchingGameId 
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const { t } = useTranslation();

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      return () => ref.removeEventListener('scroll', checkScroll);
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 600;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative px-3 sm:px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-5">
        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0">
          <span className="text-lg sm:text-xl md:text-2xl flex-shrink-0">{category.icon}</span>
          <h2 className="text-white text-sm sm:text-lg md:text-xl lg:text-2xl font-bold truncate">{category.title}</h2>
          <div className="hidden md:block h-px bg-gradient-to-r from-white/10 to-transparent w-12 lg:w-24 flex-shrink-0"></div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={onAllClick}
            className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-dark-700/80 hover:bg-primary-500/20 text-gray-300 hover:text-primary-400 text-[10px] sm:text-xs md:text-sm font-medium rounded-lg sm:rounded-xl transition-all duration-200 border border-white/5 hover:border-primary-500/30 whitespace-nowrap"
          >
            {t('landing.gameSections.viewAll')}
          </button>
          <div className="hidden sm:flex items-center gap-1">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-200 ${
                canScrollLeft 
                  ? 'bg-dark-700/80 hover:bg-dark-600 text-white border border-white/5 hover:border-white/10' 
                  : 'bg-dark-800 text-gray-600 cursor-not-allowed border border-white/5'
              }`}
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-200 ${
                canScrollRight 
                  ? 'bg-dark-700/80 hover:bg-dark-600 text-white border border-white/5 hover:border-white/10' 
                  : 'bg-dark-800 text-gray-600 cursor-not-allowed border border-white/5'
              }`}
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Games Row */}
      <div
        ref={scrollRef}
        className="flex gap-2 sm:gap-3 md:gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {category.games.map(game => (
          <div
            key={game.id}
            onClick={() => onGameClick(game)}
            className="flex-shrink-0 w-[100px] sm:w-[120px] md:w-[140px] lg:w-[150px] cursor-pointer group"
          >
            <div className="relative aspect-[3/4] rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden card-gradient group-hover:border-primary-500/30 transition-all duration-300">
              <img
                src={game.image}
                alt={getGameName(game)}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/game-placeholder.png';
                }}
              />
              {/* Gradient Overlay - theme aware */}
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/30 to-transparent opacity-60 group-hover:opacity-90 transition-opacity"></div>
              
              {/* Hover Overlay with Play Button */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                {launchingGameId === game.id ? (
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-primary-500 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30 transform scale-75 group-hover:scale-100 transition-all duration-300">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Game Info - Bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2 md:p-3">
                <p className="text-white text-[9px] sm:text-[10px] md:text-xs font-semibold truncate mb-0.5">{getGameName(game)}</p>
                <span className="text-[7px] sm:text-[8px] md:text-[10px] text-gray-400 uppercase tracking-wide font-medium truncate block">{game.provider}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GameCategories;
